import React, { useContext, useState } from "react";
import { useDateTimeDisplay } from '../../../../../platform/util/useDateTimeDisplay';
import {
  Divider,
  Typography,
  Button,
  Box,
  Container,
  List,
  ListItem,
  CardContent,
  Grid,
} from "@mui/material";
import { ChevronRight, } from "@mui/icons-material";
import "react-color-palette/lib/css/styles.css";
import "react-phone-number-input/style.css";

import InviteeQuestions from "./additionalOptions/inviteeQuestions";
import DOMPurify from 'dompurify';
import BookingTypeTime from "./bookingTypeTime";
import {
  textStyle,
  absoluteButtonStyle,
  descriptionTextStyle,
  itemHeaderStyle,
  labelStyle,
  valueStyle
} from './bookingDetails.style'; // Import styles 
import { differenceInDays } from "date-fns";
import ActionsContainer from "./bookingTypeActionContainer";
import { getShareAvailabilityOptions } from './bookingShareStatus';
import { scheduleDateLabel } from '../../../../../platform/util/bookingCalendarLabel';
import BookingBasic from "./bookingBasic";
import BookingTypeNotes from "./bookingTypeNotes";
import { useBookingTypeContext } from "./bookingTypeProvider";
import { StyledCard, StyledTypography } from "../../../../../platform/assets/css/styledComponent";
import { rootClientPath } from "../../../../../platform/util/link";
import { Recurring } from "../../../../../platform/types";
import { getValueFromKey } from "../../../../../platform/util/meetingTypes";
import BookingSummary from "./bookingSummary";
import { getSummaryAvailability } from "./bookingSummaryAvailability";
import { GetSortedWorkingHours } from "../../../../../platform/util/workingHour";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { parseDateForUI } from "../../../../../platform/util/dates/date";
import { formatDateMMDDYYYY } from "../../../../../platform/util/dates/dateFormat";

// BookingTypeEntryForm -> BookingTypeEntryFormView ->BookingBasic| BookingTypeTime
const BookingTypeEntryFormView = () => {
  const { displayCalendar } = useDateTimeDisplay();
  const {
    combinedQuestions,
    handleScheduleSettingsOpenDialog,
    selectedBusiness,
    bookingType,
    isUpdateMode,
    handleBookingBasicOpenDialog,
    setShowInviteeQuestions,
    updating,
    notificationRef,
    CancelConfirmationModal,
    selectedTab,
    setSelectedTab,
    ConfirmDialog,
    openDelete,
    setOpenDelete,
    handleConfirmDelete,
    isDisabled,
    getBookingCategory,
  } = useBookingTypeContext();


  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const isAgentCreatedBookingType = !!Number(bookingType.createdByAgentId)
  const isDisabledSegment = isDisabled || isAgentCreatedBookingType
  const { staff: availabilityStaff, group: workingHours } = getSummaryAvailability(
    bookingType, selectedBusiness?.businessStaffs || [], providerUser || {}
  );
  // Sorted working hours based on the group's timezone
  const sortedWorkingHours = GetSortedWorkingHours(
    workingHours?.workingHours || [],
    workingHours?.timeZone
  );
  // Filter the sorted working hours to return only those with onJob status equal to 1
  const onJobWorkingHours = sortedWorkingHours.filter(item => item.parent.onJob === true || Number(item.parent.onJob) === 1);

  const startDate = parseDateForUI(bookingType?.start);
  const endDate = parseDateForUI(bookingType?.end);
  const totalDays = startDate && endDate ? differenceInDays(endDate, startDate) + 1 : null;
  const locationTypeInWord = getValueFromKey(bookingType.meetingLocationTypeId)
  const url = `${rootClientPath}/${selectedBusiness?.businessBasic.url}/${bookingType?.link}`;
  return (
    <>
      <Box
        style={{ cursor: "pointer" }}
        onClick={() => {
          setSelectedTab(selectedTab !== 0 ? 0 : null);
        }}
      >
      </Box>
      <div>
        {isUpdateMode ?
          <Container maxWidth="lg">
            <StyledCard sx={{
              width: '100%',
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              maxWidth: 600,
              margin: '20px auto',
              boxShadow: { xs: 'none', sm: '0px 4px 10px rgba(0, 0, 0, 0.1)' },
              padding: { xs: '0', sm: '5px' },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                {/* ActionsContainer pushed to the right */}
                <ActionsContainer
                  isDisabled={isDisabled}
                  url={url}
                  bookingType={bookingType}
                  availabilityOptions={getShareAvailabilityOptions(bookingType, selectedBusiness?.businessStaffs)}
                  onEditSchedule={target => handleScheduleSettingsOpenDialog(target === 'hours' ? 'host' : 'setting')}
                />
              </Box>
              <CardContent sx={{ padding: .6 }}>
                {/* Header Section with Name and Color */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <StyledTypography>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: bookingType?.color }}></div>
                      <h3 style={{ fontWeight: 400 }}>
                        {bookingType?.title}
                      </h3>
                    </div>
                  </StyledTypography>
                </div>

                {/* Booking Details List */}
                <List sx={{ marginTop: 1 }}>
                  {/* Booking Basics */}

                  <ListItem
                    onClick={() => { !isDisabledSegment && handleBookingBasicOpenDialog("basic") }}
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      position: 'relative',
                      paddingBottom: { xs: 0, sm: 2 }, // Remove paddingBottom on small screens
                      gap: { xs: 0, sm: 2 }, // Remove gap on small screens
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <Typography sx={itemHeaderStyle}>
                      Booking Basics
                    </Typography>
                    <Typography>{getBookingCategory(bookingType?.bookingTypeCategoryId)}</Typography>
                    {/* Box for Duration */}
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>

                      <Typography sx={labelStyle}>
                        {`Duration:`}
                      </Typography>
                      <Typography sx={valueStyle}>
                        {bookingType?.length >= 60
                          ? `${Math.floor(bookingType.length / 60)} hour${Math.floor(bookingType.length / 60) > 1 ? 's' : ''} ${bookingType.length % 60 ? `${bookingType.length % 60} min` : ''}`
                          : `${bookingType.length} min`}
                      </Typography>

                    </Box>

                    {/* Box for Location */}
                    {locationTypeInWord && (
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 0, // Remove bottom margin
                        gap: 0, // Remove space between elements
                      }}>
                        <Typography sx={labelStyle}>
                          {`Location:`}
                        </Typography>
                        <Typography sx={valueStyle}>
                          {locationTypeInWord}
                        </Typography>
                      </Box>
                    )}

                    {/* Box for Description */}
                    <Box sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      width: "100%",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={{ ...textStyle, ...descriptionTextStyle }}>
                        <Typography sx={labelStyle}>
                          {`Description:`}
                        </Typography>
                        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bookingType?.desc) }} />
                      </Typography>
                    </Box>

                    {/* Edit Button */}
                    <Button disabled={isDisabledSegment} startIcon={<ChevronRight />} sx={absoluteButtonStyle}>
                      Edit
                    </Button>
                  </ListItem>

                  <Divider />

                  {/* Scheduling Settings */}
                  <ListItem
                    onClick={() => !isDisabledSegment && handleScheduleSettingsOpenDialog("setting")}
                    sx={{
                      flexDirection: "column", alignItems: "flex-start", position: "relative",
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <Typography sx={itemHeaderStyle}>
                      Scheduling Settings
                    </Typography>

                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={valueStyle}>
                        {bookingType?.recurring !== Recurring.NONE
                          ? `Every ${bookingType?.recurringInterval} ${bookingType?.recurringInterval > 1 ? `${bookingType?.recurring}s` : bookingType?.recurring}`
                          : "No recurrence"}
                      </Typography>
                    </Box>

                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={labelStyle}>
                        Buffer time:
                      </Typography>
                      <Typography sx={valueStyle}>
                        {bookingType?.increments} min
                      </Typography>
                    </Box>
                    <Typography sx={labelStyle}>
                      {bookingType?.end ? (
                        // When an end date exists, show the full date range along with the number of days
                        <>
                          <Typography sx={labelStyle}>
                            {endDate ? (
                              <>
                                Date range: {scheduleDateLabel(bookingType.start, bookingType, workingHours?.timeZone?.timezone, displayCalendar)} - {scheduleDateLabel(bookingType.end, bookingType, workingHours?.timeZone?.timezone, displayCalendar)}
                                {totalDays ? ` (${totalDays} day${totalDays > 1 ? "s" : ""})` : ""}
                              </>
                            ) : (
                              <>
                                Continuous Meeting: {scheduleDateLabel(bookingType.start, bookingType, workingHours?.timeZone?.timezone, displayCalendar)} (ongoing)
                              </>
                            )}
                          </Typography>                        </>
                      ) : (
                        // When no end date exists, indicate it's a continuous meeting (ongoing)
                        <>
                          Continuous Meeting: {scheduleDateLabel(bookingType.start, bookingType, workingHours?.timeZone?.timezone, displayCalendar)} (ongoing)
                        </>
                      )}
                    </Typography>

                    <Button disabled={isDisabledSegment} startIcon={<ChevronRight />} sx={absoluteButtonStyle}>
                      Edit
                    </Button>
                  </ListItem>

                  <Divider />

                  {/* Hosts and Availability */}
                  <ListItem
                    onClick={() => !isDisabledSegment && handleScheduleSettingsOpenDialog("host")}
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      position: 'relative',
                      paddingBottom: { xs: 0, sm: 2 }, // Remove paddingBottom on small screens
                      gap: { xs: 0, sm: 2 }, // Remove gap on small screens
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <Typography sx={itemHeaderStyle}>
                      Hosts and Availability
                    </Typography>

                    {/* Label and value side by side with close alignment */}
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={labelStyle}>
                        Hosts:
                      </Typography>
                      <Typography sx={valueStyle}>
                        {bookingType?.assignedStaffs?.map((staff, i) => (
                          <span key={i}>
                            {staff.name}
                            {i < bookingType?.assignedStaffs.length - 1 && ", "}
                          </span>
                        ))}
                      </Typography>
                    </Box>

                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={labelStyle}>
                        Availability hour
                      </Typography>
                    </Box>

                    {/* Edit Button */}
                    <Button disabled={isDisabledSegment} startIcon={<ChevronRight />} sx={absoluteButtonStyle}>
                      Edit
                    </Button>
                  </ListItem>

                  <Divider />

                  {/* Booking Visibility */}
                  <ListItem
                    onClick={() => !isDisabledSegment && handleScheduleSettingsOpenDialog("privacy")}
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      position: 'relative',
                      paddingBottom: { xs: 0, sm: 2 }, // Remove paddingBottom on small screens
                      gap: { xs: 0, sm: 2 }, // Remove gap on small screens
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <Typography sx={itemHeaderStyle}>
                      Booking Visibility
                    </Typography>

                    {/* Label and value side by side with close alignment */}
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={labelStyle}>
                        Visible to all staff:
                      </Typography>
                      <Typography sx={valueStyle}>
                        {bookingType?.isVisibleToStaff ? "Yes" : "No"}
                      </Typography>
                    </Box>

                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0, // Remove bottom margin
                      gap: 0, // Remove space between elements
                    }}>
                      <Typography sx={labelStyle}>
                        Displayed on Timely365 page:
                      </Typography>
                      <Typography sx={valueStyle}>
                        {bookingType?.displayOnBookingPage ? "Yes" : "No"}
                      </Typography>
                    </Box>

                    {/* Edit Button */}
                    <Button disabled={isDisabledSegment} startIcon={<ChevronRight />} sx={absoluteButtonStyle}>
                      Edit
                    </Button>
                  </ListItem>

                  <Divider />

                  {/* More Options */}
                  <ListItem
                    onClick={() => !isDisabledSegment && handleBookingBasicOpenDialog("moreOptions")}
                    sx={{ flexDirection: "column", alignItems: "flex-start", position: 'relative', pointerEvents: isDisabled ? "none" : "auto", }}
                  >
                    <Typography sx={itemHeaderStyle}>
                      More Options
                    </Typography>

                    {/* Split details into left and right groups */}
                    <Grid container spacing={0} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                      {/* Left Column */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 0, sm: 1 }, // Remove gap on small screens
                            marginBottom: { xs: 0, sm: 0.5 }, // Remove margin on small screens
                          }}
                        >
                          <Typography sx={labelStyle}>Type:</Typography>
                          <Typography sx={valueStyle}>
                            {bookingType?.bookingSize === "one" ? "One-to-One" : "Group meeting"}
                          </Typography>
                        </Box>
                        {bookingType?.bookingSize === "group" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: { xs: 0, sm: 1 }, // Remove gap on small screens
                              marginBottom: { xs: 0, sm: 0.5 }, // Remove margin on small screens
                            }}
                          >
                            <Typography sx={labelStyle}>Max Invitees:</Typography>
                            <Typography sx={valueStyle}>
                              {bookingType?.maxInvitees > 0 ? bookingType?.maxInvitees : 1}
                            </Typography>
                          </Box>

                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 0, sm: 1 }, // Remove gap on small screens
                            marginBottom: { xs: 0, sm: 0.5 }, // Remove margin on small screens
                          }}
                        >
                          <Typography sx={labelStyle}>Booking url:</Typography>
                          <Typography sx={valueStyle}>
                            {bookingType?.link}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Right Column */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 0, sm: 1 }, // Remove gap on small screens
                            marginBottom: { xs: 0, sm: 0.5 }, // Remove margin on small screens
                          }}
                        >
                          <Typography sx={labelStyle}>Price:</Typography>
                          <Typography sx={valueStyle}>
                            {bookingType?.price > 0 ? "$ " + bookingType?.price : "Free"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 0, sm: 1 }, // Remove gap on small screens
                            marginBottom: { xs: 0, sm: 0.5 }, // Remove margin on small screens
                          }}
                        >
                          <Typography sx={labelStyle}>Payment Required:</Typography>
                          <Typography sx={valueStyle}>
                            {bookingType?.isPaymentRequired ? "Yes" : "No"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>


                    <Button disabled={isDisabledSegment} startIcon={<ChevronRight />} sx={absoluteButtonStyle}>
                      Edit
                    </Button>
                  </ListItem>
                  <Divider />

                  {/* Custom Invitee Questions */}
                  <ListItem
                    onClick={() => !isDisabledSegment && setShowInviteeQuestions(true)}
                    sx={{ flexDirection: "column", alignItems: "flex-start", position: 'relative', pointerEvents: isDisabled ? "none" : "auto", }}
                  >
                    <Typography sx={itemHeaderStyle}>
                      Custom Questions
                    </Typography>
                    <Typography sx={textStyle}>
                      {combinedQuestions?.slice(0, 3).map((question, i) => (
                        <div key={i}>
                          {i + 1}. {question.title}
                        </div>
                      ))}
                    </Typography>
                    {combinedQuestions?.length > 3 && (
                      <Typography variant="caption" color="textSecondary">
                        {`+ ${combinedQuestions.length - 3} more...`}
                      </Typography>
                    )}
                    <Button disabled={isDisabledSegment} startIcon={<ChevronRight />} sx={absoluteButtonStyle}>
                      Edit
                    </Button>
                  </ListItem>
                  {/* Invitee Questions Dialog */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <InviteeQuestions />
                  </div>

                  {/* Notification for Updates */}
                  {updating && (
                    <ListItem>
                      <div ref={notificationRef} className="notification" style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', borderRadius: '5px' }}>
                        Changes Saved!
                      </div>
                    </ListItem>
                  )}
                </List>
                <BookingTypeNotes bookingTypeId={bookingType?.id} />
                <BookingSummary
                  bookingType={bookingType}
                  startDate={startDate}
                  endDate={endDate}
                  totalDays={totalDays}
                  weeklyAvailability={onJobWorkingHours} // onJobWorkingHours from your processing
                  availabilityHost={availabilityStaff?.user?.username || availabilityStaff?.name}
                  availabilityTimezone={workingHours?.timeZone?.timezone}
                  hasAvailabilitySchedule={Boolean(workingHours)}
                  shareAvailabilityOptions={getShareAvailabilityOptions(bookingType, selectedBusiness?.businessStaffs)}
                  onEditDuration={() => handleBookingBasicOpenDialog("basic")}
                  onEditDateRange={() => handleScheduleSettingsOpenDialog("setting")}
                  onEditWeeklyAvailability={() => handleScheduleSettingsOpenDialog("host")}
                />


              </CardContent>
            </StyledCard>
          </Container> :
          <div>
            <h1>You can create your booking types here</h1>
            <Button variant="outlined" color="primary" onClick={() => handleBookingBasicOpenDialog("basic")}>
              <div className="circle" style={{ backgroundColor: bookingType?.color, marginRight: 10 }}></div>  Continue Booking
            </Button>
          </div>
        }
        <CancelConfirmationModal />
        <BookingBasic />
        <BookingTypeTime />

      </div>

      <ConfirmDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default BookingTypeEntryFormView;
